/**
 * Mirrors `packages/csv-io`'s `TargetField` union — imported as a type only
 * (erased at build) so this browser bundle never pulls in that package's
 * Node-oriented runtime code (`xlsx`/PDF writers, etc.). The label list
 * below is this file's own copy to show the admin something friendlier
 * than the raw field name; `@stagvista/csv-io`'s own `TARGET_FIELDS` is
 * still cloud-api's single source of truth for which values are valid.
 */
import type { TargetField } from "@stagvista/csv-io";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  DataTable,
  Select,
  StatusBadge,
  useToast,
} from "@stagvista/ui";
import type { DataTableColumn } from "@stagvista/ui";
import { createFileRoute, useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeft, Upload } from "lucide-react";
import * as React from "react";

import { useCloudSession } from "../hooks/use-cloud-session.js";
import { CloudApiError } from "../lib/cloud-api-client.js";

const TARGET_FIELD_LABELS: ReadonlyArray<{ value: TargetField; label: string }> = [
  { value: "title", label: "Title" },
  { value: "speaker", label: "Speaker" },
  { value: "notes", label: "Notes" },
  { value: "category", label: "Category" },
  { value: "colorLabel", label: "Color label" },
  { value: "timerType", label: "Timer type" },
  { value: "durationMs", label: "Duration" },
  { value: "countdownMs", label: "Countdown" },
  { value: "timeMode", label: "Time mode" },
  { value: "pinnedStart", label: "Start time" },
  { value: "warnAtMs", label: "Warn at" },
  { value: "criticalAtMs", label: "Critical at" },
  { value: "autoStart", label: "Auto start" },
  { value: "autoNext", label: "Auto next" },
  { value: "overtimeEnabled", label: "Overtime enabled" },
];

const IGNORE_VALUE = "__ignore__";

export const Route = createFileRoute("/services/$serviceId/import")({
  component: ImportCsvPage,
});

/** The rows displayed in the preview grid are capped for render performance — the commit step still processes every row the server validated, regardless of how many are shown here. */
const MAX_DISPLAYED_ROWS = 200;

type RowStatus = "ok" | "warning" | "error";

interface ValidatedRowDto {
  readonly rowIndex: number;
  readonly status: RowStatus;
  readonly reasons: string[];
  readonly data: {
    readonly title?: string;
    readonly speaker?: string;
    readonly durationMs?: number;
  };
}

/** `apps/cloud-api`'s `PreviewCsvImportResult` response shape — only the fields this page reads. */
interface PreviewResultDto {
  readonly headers: string[];
  readonly mapping: Record<string, TargetField>;
  readonly summary: {
    readonly total: number;
    readonly ok: number;
    readonly warning: number;
    readonly error: number;
  };
  readonly rows: ValidatedRowDto[];
}

/** This page's own editable copy of a mapping — every detected header gets an entry, `IGNORE_VALUE` standing in for "not mapped" so the `<Select>` always has a real, non-empty value to control. */
type EditableMapping = Record<string, TargetField | typeof IGNORE_VALUE>;

function seedMapping(result: PreviewResultDto): EditableMapping {
  const seeded: EditableMapping = {};
  for (const header of result.headers) {
    seeded[header] = result.mapping[header] ?? IGNORE_VALUE;
  }
  return seeded;
}

/** `ColumnMapping` (`Partial<Record<string, TargetField>>`) never spells out "ignored" — an omitted key means exactly that — so this strips every `IGNORE_VALUE` entry before the mapping goes anywhere near a request body. */
function cleanMapping(mapping: EditableMapping): Record<string, TargetField> {
  const cleaned: Record<string, TargetField> = {};
  for (const [header, value] of Object.entries(mapping)) {
    if (value !== IGNORE_VALUE) cleaned[header] = value;
  }
  return cleaned;
}

/** `apps/cloud-api`'s `CommitCsvImportResult` response shape. */
interface CommitResultDto {
  readonly importedCount: number;
}

const STATUS_TONE: Record<RowStatus, "success" | "warning" | "critical"> = {
  ok: "success",
  warning: "warning",
  error: "critical",
};

function ImportCsvPage(): React.ReactElement {
  const { session } = useCloudSession();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (!session) void navigate({ to: "/sign-in" });
  }, [session, navigate]);

  if (!session) return <></>;
  return <ImportCsvView />;
}

/** Strips the `data:...;base64,` prefix `FileReader.readAsDataURL` adds — the server wants the raw base64 payload only. */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result.slice(result.indexOf(",") + 1));
    };
    reader.onerror = () => reject(new Error("Could not read the file."));
    reader.readAsDataURL(file);
  });
}

function ImportCsvView(): React.ReactElement {
  const { serviceId } = useParams({ from: "/services/$serviceId/import" });
  const { client } = useCloudSession();
  const { toast } = useToast();
  const navigate = useNavigate();

  const [fileBase64, setFileBase64] = React.useState<string | null>(null);
  const [fileName, setFileName] = React.useState<string | null>(null);
  const [preview, setPreview] = React.useState<PreviewResultDto | null>(null);
  const [mapping, setMapping] = React.useState<EditableMapping>({});
  const [previewError, setPreviewError] = React.useState<string | null>(null);
  const [loadingPreview, setLoadingPreview] = React.useState(false);
  const [committing, setCommitting] = React.useState(false);

  function reportError(err: unknown, fallback: string): string {
    return err instanceof CloudApiError ? (err.problem.detail ?? err.problem.title) : fallback;
  }

  async function handleFileSelected(event: React.ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = event.target.files?.[0];
    if (!file) return;

    setFileName(file.name);
    setPreview(null);
    setMapping({});
    setPreviewError(null);
    setLoadingPreview(true);
    try {
      const base64 = await fileToBase64(file);
      setFileBase64(base64);
      const result = await client.request<PreviewResultDto>(
        "POST",
        `/v1/services/${serviceId}/import/preview`,
        { fileBase64: base64, referenceDate: new Date().toISOString() },
      );
      setPreview(result);
      setMapping(seedMapping(result));
    } catch (err) {
      setPreviewError(reportError(err, "Couldn't preview this file."));
    } finally {
      setLoadingPreview(false);
    }
  }

  /** Re-runs preview against the corrected mapping so the row-status/summary counts reflect what would actually be imported — never persisted (§ `csvImportBodySchema`'s own doc comment), just re-sent verbatim at commit time. */
  async function handleMappingChange(
    header: string,
    value: TargetField | typeof IGNORE_VALUE,
  ): Promise<void> {
    const next = { ...mapping, [header]: value };
    setMapping(next);
    if (!fileBase64) return;
    setLoadingPreview(true);
    setPreviewError(null);
    try {
      const result = await client.request<PreviewResultDto>(
        "POST",
        `/v1/services/${serviceId}/import/preview`,
        { fileBase64, referenceDate: new Date().toISOString(), mapping: cleanMapping(next) },
      );
      setPreview(result);
    } catch (err) {
      setPreviewError(reportError(err, "Couldn't re-preview with this mapping."));
    } finally {
      setLoadingPreview(false);
    }
  }

  async function handleCommit(): Promise<void> {
    if (!fileBase64) return;
    setCommitting(true);
    try {
      const result = await client.request<CommitResultDto>(
        "POST",
        `/v1/services/${serviceId}/import/commit`,
        { fileBase64, referenceDate: new Date().toISOString(), mapping: cleanMapping(mapping) },
      );
      toast({
        tone: "success",
        title: "Import complete",
        description: `${result.importedCount} item(s) imported.`,
      });
      // The event id isn't known client-side on this route (only serviceId
      // is) — back to the events list, not the specific event's page.
      await navigate({ to: "/" });
    } catch (err) {
      toast({
        tone: "error",
        title: "Import failed",
        description: reportError(err, "Couldn't commit this import."),
      });
    } finally {
      setCommitting(false);
    }
  }

  const columns: DataTableColumn<ValidatedRowDto>[] = [
    { key: "row", header: "Row", render: (row) => row.rowIndex + 1, width: "60px" },
    {
      key: "status",
      header: "Status",
      render: (row) => <StatusBadge tone={STATUS_TONE[row.status]}>{row.status}</StatusBadge>,
      width: "110px",
    },
    { key: "title", header: "Title", render: (row) => row.data.title ?? "—" },
    { key: "speaker", header: "Speaker", render: (row) => row.data.speaker ?? "—" },
    {
      key: "duration",
      header: "Duration",
      render: (row) =>
        row.data.durationMs !== undefined ? `${row.data.durationMs / 60_000} min` : "—",
      width: "90px",
    },
    { key: "reasons", header: "Notes", render: (row) => row.reasons.join("; ") || "—" },
  ];

  const hasErrors = (preview?.summary.error ?? 0) > 0;

  return (
    <div className="mx-auto flex max-w-2xl flex-col gap-4 p-6">
      <div className="flex items-center justify-between">
        <h1 className="text-h1 text-fg-primary">Import schedule from CSV</h1>
        <Button variant="ghost" size="sm" onClick={() => void navigate({ to: "/" })}>
          <ArrowLeft className="size-4" />
          Back to events
        </Button>
      </div>

      <p className="text-caption text-fg-tertiary">
        Rows are added to the end of this service's existing schedule — nothing is replaced or
        removed.
      </p>

      <Card>
        <CardHeader>
          <CardTitle>Choose a file</CardTitle>
        </CardHeader>
        <CardBody>
          <label className="flex cursor-pointer flex-col gap-2">
            <span className="flex items-center gap-2 text-body text-fg-secondary">
              <Upload className="size-4" />
              {fileName ?? "Select a .csv file"}
            </span>
            <input
              type="file"
              accept=".csv,text/csv"
              className="text-caption text-fg-tertiary file:mr-3 file:rounded-md file:border file:border-line file:bg-surface-overlay file:px-3 file:py-1.5 file:text-caption file:text-fg-primary"
              onChange={(e) => void handleFileSelected(e)}
            />
          </label>
        </CardBody>
      </Card>

      {loadingPreview ? <p className="text-caption text-fg-tertiary">Previewing…</p> : null}
      {previewError ? <p className="text-caption text-status-critical">{previewError}</p> : null}

      {preview ? (
        <>
          <Card>
            <CardHeader>
              <CardTitle>Column mapping</CardTitle>
            </CardHeader>
            <CardBody>
              <p className="mb-3 text-caption text-fg-tertiary">
                Columns are auto-detected from the file's header row — fix any that guessed wrong
                before importing.
              </p>
              <div className="flex flex-col gap-2">
                {preview.headers.map((header) => (
                  <div key={header} className="flex items-center gap-3">
                    <span className="w-40 shrink-0 truncate text-body text-fg-secondary">
                      {header}
                    </span>
                    <span className="text-fg-tertiary">→</span>
                    <Select
                      aria-label={`Map column "${header}"`}
                      value={mapping[header] ?? IGNORE_VALUE}
                      onChange={(value) => void handleMappingChange(header, value)}
                      options={[{ value: IGNORE_VALUE, label: "Ignore" }, ...TARGET_FIELD_LABELS]}
                    />
                  </div>
                ))}
              </div>
            </CardBody>
          </Card>

          <div className="flex gap-3 text-caption text-fg-tertiary">
            <span>{preview.summary.total} row(s)</span>
            <span className="text-status-success">{preview.summary.ok} ok</span>
            <span className="text-status-warning">{preview.summary.warning} warning</span>
            <span className="text-status-critical">{preview.summary.error} error</span>
          </div>

          <DataTable
            aria-label="CSV preview rows"
            columns={columns}
            rows={preview.rows.slice(0, MAX_DISPLAYED_ROWS)}
            getRowKey={(row) => String(row.rowIndex)}
            emptyTitle="No rows found"
          />
          {preview.rows.length > MAX_DISPLAYED_ROWS ? (
            <p className="text-caption text-fg-tertiary">
              Showing the first {MAX_DISPLAYED_ROWS} of {preview.rows.length} rows — every row will
              still be imported.
            </p>
          ) : null}

          <Button
            className="self-start"
            disabled={hasErrors}
            loading={committing}
            title={hasErrors ? "Fix the errored rows in the source file and re-upload" : undefined}
            onClick={() => void handleCommit()}
          >
            Commit Import
          </Button>
        </>
      ) : null}
    </div>
  );
}
